import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/Toast';
import { 
  Monitor, 
  Shield, 
  Eye,
  EyeOff,
  Send,
  Upload,
  Download,
  Power,
  Wifi,
  WifiOff,
  AlertTriangle,
  CheckCircle,
  XCircle,
  FileUp,
  Headphones,
  PhoneCall,
  X
} from 'lucide-react';

const API_URL = BACKEND_URL;

export const RemoteSupportClientWidget = () => {
  const { user, tenant } = useAuth();
  const toast = useToast();
  
  // Estado do widget
  const [isOpen, setIsOpen] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [problemDescription, setProblemDescription] = useState('');
  const [remoteId, setRemoteId] = useState('');
  const [remotePassword, setRemotePassword] = useState('');
  const [ticketId, setTicketId] = useState(null);
  
  // Estado do suporte
  const [supportEnabled, setSupportEnabled] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [supporterName, setSupporterName] = useState('');
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [pendingPermission, setPendingPermission] = useState(null);
  const [waitingForSupport, setWaitingForSupport] = useState(false);
  
  // Controles permitidos
  const [allowMouse, setAllowMouse] = useState(false);
  const [allowKeyboard, setAllowKeyboard] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  
  // WebRTC
  const peerConnection = useRef(null);
  const dataChannel = useRef(null);
  const fileChannel = useRef(null);
  const mediaStream = useRef(null);
  
  // Chat
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  
  // Arquivos
  const [receivedFiles, setReceivedFiles] = useState([]);

  // Solicitar suporte
  const requestSupport = async () => {
    if (!problemDescription.trim()) {
      toast.error('Descreva o problema para solicitar suporte');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/support/tickets`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tenant_id: tenant?.id || 'default',
          tenant_name: tenant?.name || 'Cliente',
          user_email: user?.email,
          problem_description: problemDescription,
          remote_id: remoteId || null,
          remote_password: remotePassword || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setTicketId(data.ticket_id);
        setSupportEnabled(true);
        setWaitingForSupport(true);
        setShowRequestForm(false);
        setIsOpen(false);
        toast.success('Chamado aberto! Aguarde o suporte responder...');
        startPolling();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao abrir chamado');
      }
    } catch (error) {
      console.error('Erro ao solicitar suporte:', error);
      toast.error('Erro ao abrir chamado');
    }
  };

  // Cancelar solicitação
  const cancelRequest = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/remote-support/unregister`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setSupportEnabled(false);
      setWaitingForSupport(false);
      setProblemDescription('');
      stopPolling();
      toast.info('Solicitação cancelada');
    } catch (error) {
      console.error('Erro ao cancelar:', error);
    }
  };

  const pollingRef = useRef(null);

  const startPolling = () => {
    pollingRef.current = setInterval(async () => {
      await checkForConnection();
    }, 2000);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      disconnect();
    };
  }, []);

  const checkForConnection = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/remote-support/check-connection`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.has_connection && !isConnected) {
          // Suporte quer conectar
          setPendingPermission(data);
          setShowPermissionModal(true);
        }
      }
    } catch (error) {
      console.error('Erro ao verificar conexão:', error);
    }
  };

  // Aceitar conexão do suporte
  const acceptConnection = async () => {
    setShowPermissionModal(false);
    setSupporterName(pendingPermission?.supporter_name || 'Suporte');
    
    try {
      // Iniciar compartilhamento de tela
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: 'always' },
        audio: false
      });
      
      mediaStream.current = stream;
      
      // Detectar quando usuário para o compartilhamento
      stream.getVideoTracks()[0].onended = () => {
        disconnect();
        toast.info('Compartilhamento de tela encerrado');
      };
      
      await initializeWebRTC(stream);
      setIsSharing(true);
      setIsConnected(true);
      setWaitingForSupport(false);
      toast.success('Conectado ao suporte!');
      
    } catch (error) {
      console.error('Erro ao compartilhar tela:', error);
      toast.error('Erro ao compartilhar tela');
      rejectConnection();
    }
  };

  const rejectConnection = async () => {
    setShowPermissionModal(false);
    setPendingPermission(null);
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/remote-support/reject-connection`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Erro ao rejeitar conexão:', error);
    }
  };

  // Inicializar WebRTC
  const initializeWebRTC = async (stream) => {
    const config = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    };
    
    peerConnection.current = new RTCPeerConnection(config);
    
    // Adicionar stream de vídeo
    stream.getTracks().forEach(track => {
      peerConnection.current.addTrack(track, stream);
    });
    
    // Handler para canal de dados
    peerConnection.current.ondatachannel = (event) => {
      const channel = event.channel;
      
      if (channel.label === 'control') {
        dataChannel.current = channel;
        channel.onmessage = (e) => handleControlMessage(JSON.parse(e.data));
      } else if (channel.label === 'files') {
        fileChannel.current = channel;
        channel.onmessage = (e) => handleFileMessage(e.data);
      }
    };
    
    // ICE candidates
    peerConnection.current.onicecandidate = async (event) => {
      if (event.candidate) {
        await sendSignal('ice-candidate', event.candidate);
      }
    };
    
    // Aguardar oferta do suporte
    await waitForOffer();
  };

  const waitForOffer = async () => {
    const checkOffer = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/remote-support/get-offer`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.offer) {
            clearInterval(checkOffer);
            await peerConnection.current.setRemoteDescription(
              new RTCSessionDescription(data.offer)
            );
            
            const answer = await peerConnection.current.createAnswer();
            await peerConnection.current.setLocalDescription(answer);
            await sendSignal('answer', answer);
          }
        }
      } catch (error) {
        console.error('Erro ao aguardar oferta:', error);
      }
    }, 1000);
  };

  const sendSignal = async (type, data) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/remote-support/client-signal`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type, data })
      });
    } catch (error) {
      console.error('Erro ao enviar sinal:', error);
    }
  };

  // Processar mensagem de controle
  const handleControlMessage = (message) => {
    if (message.type === 'chat') {
      setMessages(prev => [...prev, { 
        from: 'support', 
        text: message.text, 
        time: new Date().toLocaleTimeString() 
      }]);
    } else if (message.type === 'mouse' && allowMouse) {
      simulateMouseEvent(message);
    } else if (message.type === 'keyboard' && allowKeyboard) {
      simulateKeyboardEvent(message);
    }
  };

  // Simular evento de mouse
  const simulateMouseEvent = (data) => {
    const x = data.x * window.innerWidth;
    const y = data.y * window.innerHeight;
    
    const element = document.elementFromPoint(x, y);
    if (!element) return;
    
    const eventInit = {
      bubbles: true,
      cancelable: true,
      clientX: x,
      clientY: y,
      button: data.button || 0
    };
    
    if (data.event === 'click' || data.event === 'mousedown') {
      element.dispatchEvent(new MouseEvent('mousedown', eventInit));
      element.dispatchEvent(new MouseEvent('mouseup', eventInit));
      element.dispatchEvent(new MouseEvent('click', eventInit));
    } else if (data.event === 'mousemove') {
      element.dispatchEvent(new MouseEvent('mousemove', eventInit));
    } else if (data.event === 'contextmenu') {
      element.dispatchEvent(new MouseEvent('contextmenu', eventInit));
    }
  };

  // Simular evento de teclado
  const simulateKeyboardEvent = (data) => {
    const eventInit = {
      bubbles: true,
      cancelable: true,
      key: data.key,
      code: data.code,
      ctrlKey: data.ctrlKey,
      altKey: data.altKey,
      shiftKey: data.shiftKey
    };
    
    if (data.event === 'keydown') {
      document.activeElement?.dispatchEvent(new KeyboardEvent('keydown', eventInit));
    } else if (data.event === 'keyup') {
      document.activeElement?.dispatchEvent(new KeyboardEvent('keyup', eventInit));
    }
  };

  // Processar arquivo recebido
  const receivedChunks = useRef([]);
  const currentFileInfo = useRef(null);
  
  const handleFileMessage = (data) => {
    if (typeof data === 'string') {
      const info = JSON.parse(data);
      if (info.type === 'file-start') {
        currentFileInfo.current = info;
        receivedChunks.current = [];
      } else if (info.type === 'file-end') {
        const blob = new Blob(receivedChunks.current);
        const url = URL.createObjectURL(blob);
        setReceivedFiles(prev => [...prev, {
          name: currentFileInfo.current.name,
          url,
          size: currentFileInfo.current.size
        }]);
        toast.success(`Arquivo ${currentFileInfo.current.name} recebido!`);
      }
    } else {
      receivedChunks.current.push(data);
    }
  };

  // Enviar mensagem
  const sendChatMessage = () => {
    if (!newMessage.trim() || !dataChannel.current) return;
    
    dataChannel.current.send(JSON.stringify({
      type: 'chat',
      text: newMessage
    }));
    
    setMessages(prev => [...prev, { 
      from: 'client', 
      text: newMessage, 
      time: new Date().toLocaleTimeString() 
    }]);
    setNewMessage('');
  };

  // Desconectar
  const disconnect = () => {
    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach(track => track.stop());
      mediaStream.current = null;
    }
    
    if (dataChannel.current) {
      dataChannel.current.close();
    }
    
    if (fileChannel.current) {
      fileChannel.current.close();
    }
    
    if (peerConnection.current) {
      peerConnection.current.close();
    }
    
    setIsConnected(false);
    setIsSharing(false);
    setAllowMouse(false);
    setAllowKeyboard(false);
    setMessages([]);
    setSupporterName('');
    setSupportEnabled(false);
    setWaitingForSupport(false);
    setProblemDescription('');
    stopPolling();
  };

  // Não mostrar para super_admin
  if (user?.role === 'super_admin') {
    return null;
  }

  return (
    <>
      {/* Modal de Permissão */}
      {showPermissionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-96">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-500" />
                Suporte Quer Conectar
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <p className="text-lg font-medium">
                  {pendingPermission?.supporter_name || 'Suporte Técnico'}
                </p>
                <p className="text-slate-500">
                  está pronto para te ajudar!
                </p>
              </div>
              
              <div className="bg-amber-50 p-3 rounded-lg text-sm">
                <AlertTriangle className="w-4 h-4 inline mr-2 text-amber-500" />
                Você precisará compartilhar sua tela. Pode encerrar a qualquer momento.
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={rejectConnection}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Recusar
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={acceptConnection}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Aceitar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Botão flutuante para solicitar suporte */}
      {!isOpen && !isConnected && !waitingForSupport && (
        <button
          onClick={() => setIsOpen(true)}
          title="Precisa de Ajuda?"
          className="fixed bottom-6 right-6 z-40 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg flex items-center transition-all hover:scale-105 group"
        >
          <Headphones className="w-6 h-6 shrink-0" />
          <span className="font-medium overflow-hidden whitespace-nowrap max-w-0 group-hover:max-w-[160px] group-hover:ml-2 transition-all duration-300">Precisa de Ajuda?</span>
        </button>
      )}

      {/* Widget de solicitação de suporte */}
      {isOpen && !isConnected && !waitingForSupport && (
        <div className="fixed bottom-6 right-6 z-40">
          <Card className="w-96 shadow-xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Headphones className="w-5 h-5 text-blue-600" />
                  Solicitar Suporte
                </CardTitle>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <CardDescription>
                Nossa equipe vai te ajudar remotamente
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="problem" className="text-sm font-medium">
                  Descreva seu problema: *
                </Label>
                <Textarea
                  id="problem"
                  placeholder="Ex: Não consigo finalizar uma venda no PDV..."
                  value={problemDescription}
                  onChange={(e) => setProblemDescription(e.target.value)}
                  className="mt-1 h-20"
                />
              </div>
              
              {/* Seção de Acesso Remoto */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Monitor className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium">Acesso Remoto (Opcional)</span>
                </div>
                
                <div className="bg-green-50 p-3 rounded-lg text-xs text-green-700 mb-3">
                  <p className="font-medium mb-1">Para acesso completo à sua máquina:</p>
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>Baixe o <a href="https://rustdesk.com/download" target="_blank" rel="noopener noreferrer" className="underline font-medium">RustDesk</a> (gratuito)</li>
                    <li>Abra o programa e copie o <strong>ID</strong> e <strong>Senha</strong></li>
                    <li>Cole nos campos abaixo</li>
                  </ol>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="remoteId" className="text-xs">ID do RustDesk</Label>
                    <Input
                      id="remoteId"
                      placeholder="Ex: 123456789"
                      value={remoteId}
                      onChange={(e) => setRemoteId(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="remotePassword" className="text-xs">Senha</Label>
                    <Input
                      id="remotePassword"
                      type="password"
                      placeholder="******"
                      value={remotePassword}
                      onChange={(e) => setRemotePassword(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
              
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={requestSupport}
                disabled={!problemDescription.trim()}
              >
                <PhoneCall className="w-4 h-4 mr-2" />
                Abrir Chamado de Suporte
              </Button>
              
              <p className="text-xs text-slate-500 text-center">
                {remoteId ? 'O suporte terá acesso total à sua máquina via RustDesk' : 'Informe os dados do RustDesk para acesso remoto'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Widget aguardando conexão */}
      {waitingForSupport && (
        <div className="fixed bottom-6 right-6 z-40">
          <Card className="w-80 shadow-xl border-blue-200">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="relative mx-auto w-16 h-16">
                  <div className="absolute inset-0 bg-blue-100 rounded-full animate-ping"></div>
                  <div className="relative bg-blue-600 rounded-full w-16 h-16 flex items-center justify-center">
                    <Headphones className="w-8 h-8 text-white" />
                  </div>
                </div>
                
                <div>
                  <p className="font-medium">Chamado Aberto!</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Aguardando resposta do suporte
                  </p>
                </div>
                
                <div className="bg-slate-50 rounded-lg p-3 text-left">
                  <p className="text-xs text-slate-500">Seu problema:</p>
                  <p className="text-sm">{problemDescription}</p>
                </div>
                
                {remoteId && (
                  <div className="bg-green-50 rounded-lg p-3 text-left">
                    <p className="text-xs text-green-600 font-medium">RustDesk configurado:</p>
                    <p className="text-sm font-mono">ID: {remoteId}</p>
                    <p className="text-xs text-green-600 mt-1">O suporte poderá acessar sua máquina</p>
                  </div>
                )}
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={cancelRequest}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancelar Chamado
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default RemoteSupportClientWidget;
